// Tiny typed event emitter that backs window.airys.on/off plus the
// internal hooks the panel uses to surface lifecycle changes (open /
// close / conversation-resolved).
//
// Two design choices worth noting:
//   1. No payloads — every event is a signal. Host code reads its own
//      state to discover what changed. Adding payloads later is a
//      non-breaking change (handlers ignore extra args).
//   2. We DON'T use the DOM's EventTarget. It's awkward to type, drags
//      the wrapper-class boilerplate (CustomEvent) into customer
//      code, and is harder to clear in one call at shutdown.

import type { WidgetEvent } from './types'

export interface Emitter {
  /** Subscribe to an event. Returns an unsubscribe function. */
  on: (event: WidgetEvent, cb: () => void) => () => void
  /** Remove a previously-registered listener. */
  off: (event: WidgetEvent, cb: () => void) => void
  /** Fire `event` to every subscribed listener. */
  emit: (event: WidgetEvent) => void
  /** Drop every subscriber. Used at shutdown so a fresh re-mount
   *  doesn't double-fire to closures the customer may have leaked. */
  clear: () => void
}

export function createEmitter(): Emitter {
  // Map<event, Set<cb>> — set semantics make double-subscribe a no-op
  // and let off() be O(1).
  const listeners = new Map<WidgetEvent, Set<() => void>>()

  function on(event: WidgetEvent, cb: () => void): () => void {
    let set = listeners.get(event)
    if (!set) {
      set = new Set()
      listeners.set(event, set)
    }
    set.add(cb)
    return () => off(event, cb)
  }

  function off(event: WidgetEvent, cb: () => void): void {
    listeners.get(event)?.delete(cb)
  }

  function emit(event: WidgetEvent): void {
    const set = listeners.get(event)
    if (!set || set.size === 0) return
    // Snapshot before iterating so handlers that unsubscribe themselves
    // mid-emit don't perturb the iteration.
    for (const cb of Array.from(set)) {
      try {
        cb()
      } catch (err) {
        // A throwing handler shouldn't kill the rest of the chain.
        // eslint-disable-next-line no-console
        console.error('[airys widget] event handler threw:', err)
      }
    }
  }

  function clear(): void {
    listeners.clear()
  }

  return { on, off, emit, clear }
}
